import { Observable, of, combineLatest } from 'rxjs';
import { map, catchError, mergeMap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { InjectionToken } from '@angular/core';
import { HttpResponse, HttpErrorResponse } from '@angular/common/http';
import * as i0 from "@angular/core";
export const APP_MAX_EXPIRATION_TIME = new InjectionToken('maxExpirationTIme', { providedIn: 'root', factory: () => 3600 * 30 });
/**
 * A service that implements a simple Wrapper for check online status.
 *
 * @export
 * @interface CacheStorage
 */
export class IsOnlineService {
    /**
     * A method to check if the connection is online.
     *
     * @returns {Observable<boolean>}
     * @memberof IsOnlineService
     */
    isOnline() {
        return of(window.navigator.onLine);
    }
}
IsOnlineService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function IsOnlineService_Factory() { return new IsOnlineService(); }, token: IsOnlineService, providedIn: "root" });
/**
 * A service that implements a simple Wrapper for localStorage and make ir override in Ionic App.
 *
 * @export
 * @interface CacheStorage
 */
export class CacheStorageLayer {
    /**
     * Method for get an item.
     *
     * @param {string} key
     * @returns {(any | Promise<any>)}
     * @memberof CacheStorageLayer
     */
    getItem(key) {
        return of(JSON.parse(localStorage.getItem(key)));
    }
    /**
     * Method for set a item.
     *
     * @param {string} key the key
     * @memberof CacheStorageLayer
     */
    setItem(key, value) {
        localStorage.setItem(key, value);
    }
    /**
     * A method for clear the storage.
     *
     * @returns {Promise<void>}
     * @memberof CacheStorageLayer
     */
    clearCache() {
        return new Promise((resolve) => resolve(localStorage.clear()));
    }
}
CacheStorageLayer.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function CacheStorageLayer_Factory() { return new CacheStorageLayer(); }, token: CacheStorageLayer, providedIn: "root" });
/**
 * Interceptor for perform cache
 *
 * @export
 * @class CacheInterceptorService
 * @implements {HttpInterceptor}
 */
export class CacheInterceptorService {
    constructor(maxExpiration, storage, onlineService) {
        this.maxExpiration = maxExpiration;
        this.storage = storage;
        this.onlineService = onlineService;
        /**
         * Tge tune fir expiration map based on content from the API.
         *
         * @memberof CacheInterceptorService
         */
        this.expiration_time_map = { 'COUNTRIES': 3600 * 24 * 7 };
    }
    /**
     * Method of interceptor of save data
     *
     * @param {HttpResponse<any>} a
     * @param {HttpRequest<any>} request
     * @returns
     * @memberof CacheInterceptorService
     */
    save(a, request) {
        if (a instanceof HttpResponse) {
            const { isValid, message } = this.isValidObject(a, request);
            if (!isValid) {
                throw (message);
            }
            const toSave = {
                expiration: this.expiration_time_map[request.params.get('content')] !== undefined ?
                    (new Date().getTime() + this.expiration_time_map[request.params.get('content')] * 1000) :
                    new Date().getTime() + (this.maxExpiration * 1000)
            };
            Object.assign(toSave, a.body);
            try {
                this.storage.setItem(request.urlWithParams, JSON.stringify(toSave));
            }
            catch (e) {
            }
            if (a)
                return a;
        }
        else {
            return this.returnCachedResponse(request, a);
        }
    }
    /**
     * Method for check if the response is valid from some endpoints.
     * It doesn't make validation on nested objects.
     *
     * @param {HttpResponse<any>} a
     * @param {HttpRequest<any>} request
     * @returns
     * @memberof CacheInterceptorService
     */
    isValidObject(a, request) {
        let isValid = false;
        let message = {};
        if (this.requestIsFromAPI(request)) {
            // We need to check if items is object or array. Some endpoints returns a sinlge value.
            isValid = a.body.items != undefined && a.body.pageInfo != undefined && (Array.isArray(a.body.items) || typeof a.body.items === 'object');
            message = { message: `Request to ${request.urlWithParams} failed. Expected to contain pageInfo and items but these weren't found. Mocking to no results`, body: a.body };
        }
        if (this.requestIsAthletePhotos(request) || this.requestIsAthleteNewsTeaser(request)) {
            isValid = a.body.pager != undefined && a.body.results != undefined && Array.isArray(a.body.results);
            message = { message: `Request to ${request.urlWithParams} failed. Expected to contain pager and results but these weren't found. Mocking to no results`, body: a.body };
        }
        if (this.requestIsNews(request)) {
            isValid = Array.isArray(a.body);
            message = { message: `Request to ${request.urlWithParams} failed. Expected to contain array. Mocking to no results`, body: a.body };
        }
        return { isValid: isValid, message: message };
    }
    /**
     * Method for get the data using a key from the storager layer.
     *
     * @param {string} key
     * @returns {Observable<any>}
     * @memberof CacheInterceptorService
     */
    getData(key) {
        return this.storage.getItem(key);
    }
    /**
     * Method for return the cached response if present or proceed to request.
     *
     * @param {HttpRequest<any>} request
     * @param {HttpResponse<any>} a
     * @returns
     * @memberof CacheInterceptorService
     */
    returnCachedResponse(request, a) {
        return this.getData(request.urlWithParams).pipe(mergeMap(data => {
            if (data) {
                return of(new HttpResponse({ body: data }));
            }
            else {
                return of(a);
            }
        }));
    }
    /**
      From ChatGPT:
   
   
   This Angular HTTP interceptor is designed to handle different scenarios for HTTP requests. It performs several checks, manipulations, and transformations based on the request parameters and URL. Here’s a detailed explanation of how it works:
   
   ### Code Breakdown
   
   1. **Initial Checks**:
      ```typescript
      if (request.params.has("content") === false &&
        !([environment.youtube_api_url, environment.news_api.athlete_news_teaser, environment.photosAPI,
          environment.news_api.continental_association_teaser, environment.news_api.member_association_teaser
        ].findIndex(url => request.url.includes(url)) > -1) || request.params.get('live_flag')) {
      ```
      - The code first checks if the request does not have a parameter called `content`.
      - Then, it checks if the request URL does not include any of the specific URLs listed (from the environment configuration) or if the request has a `live_flag` parameter.
   
   2. **Removing `live_flag` Parameter**:
      ```typescript
      const params = request.params.delete('live_flag');
      const req = request.clone({params:params});
      return next.handle(req).pipe();
      ```
      - If the above conditions are met, the interceptor removes the `live_flag` parameter from the request.
      - It then clones the request with the updated parameters and passes it to the next handler.
   
   3. **Combining Data and Online Status**:
      ```typescript
      return combineLatest(this.getData(request.urlWithParams), this.onlineService.isOnline()).pipe(
        mergeMap(([a, b]) => {
      ```
      - If the initial conditions are not met, the interceptor combines the result of `this.getData(request.urlWithParams)` (which fetches some data based on the request URL) and the online status (`this.onlineService.isOnline()`).
   
   4. **Handling Combined Results**:
      ```typescript
      switch (a != undefined) {
        case true:
          const data = (a);
          if ((data.expiration < new Date().getTime()) && b) {
            return next.handle(request).pipe(
              map(a => {
                if (a instanceof HttpErrorResponse) {
                  return this.save(a as any, request);
                } else {
                  return a;
                }
              }),
              map(a => {
                return this.save(a as any, request) as any;
              }));
          } else {
            return of(new HttpResponse({ body: data }));
          }
        case false:
          return next.handle(request).pipe(
            map(a => {
              if (a instanceof HttpErrorResponse) {
                return this.save(a as any, request);
              } else {
                return a;
              }
            }),
            map(a => {
              return this.save(a as any, request);
            }));
      }
      ```
      - If `a` (the result from `getData`) is defined:
        - It checks if the data has expired and if the user is online (`b` is true).
        - If so, it proceeds with the request, saving any errors or responses.
        - If the data has not expired, it returns a cached response.
      - If `a` is not defined, it proceeds with the request, saving any errors or responses.
   
   5. **Error Handling**:
      ```typescript
      catchError(response => {
        if (this.requestIsAthleteNewsTeaser(request) || this.requestIsAthletePhotos(request)) {
          return of(new HttpResponse({ body: { pager: { pages: 0, current_page: 0, next_page: 0, count: 0, }, results: [], error: true } }));
        }
        if (this.requestIsNews(request)) {
          return of(new HttpResponse({ body: [] }));
        }
        if (this.requestIsFromAPI(request)) {
          return of(new HttpResponse({
            body: {
              error: true,
              items: [], "pageInfo": {
                "totalResults": 0,
                "resultsPerPage": 0,
                "page": 0
              },
            }
          }));
        }
        return response;
      })
      ```
      - In case of an error, the interceptor checks the type of request and returns appropriate default responses:
        - For athlete news teaser or photos requests, it returns an empty pager and results.
        - For news requests, it returns an empty array.
        - For other API requests, it returns an error object with empty items and page information.
      - If none of these conditions are met, it simply returns the error response.
   
   ### Summary
   This interceptor is designed to:
   
   1. Filter out requests with certain parameters or URLs.
   2. Remove specific parameters from the request.
   3. Combine data retrieval with online status checks.
   4. Handle responses based on cached data and online status.
   5. Provide specific error handling based on the type of request.
   
   This approach allows for a complex and highly customizable way to manage HTTP requests and responses in an Angular application.
   
   
   
     */
    /**
     * {@inheritdoc}
     */
    intercept(request, next) {
        if (request.params.has("content") === false &&
            !([environment.youtube_api_url, environment.news_api.athlete_news_teaser, environment.photosAPI,
                environment.news_api.continental_association_teaser, environment.news_api.member_association_teaser
            ].findIndex(url => request.url.includes(url)) > -1) || request.params.get('live_flag')) {
            const params = request.params.delete('live_flag');
            const req = request.clone({ params: params });
            return next.handle(req).pipe();
        }
        return combineLatest(this.getData(request.urlWithParams), this.onlineService.isOnline()).pipe(mergeMap(([a, b]) => {
            // tslint:disable-next-line:triple-equals
            switch (a != undefined) {
                case true:
                    const data = (a);
                    if ((data.expiration < new Date().getTime()) && b) {
                        return next.handle(request).pipe(map(a => {
                            if (a instanceof HttpErrorResponse) {
                                return this.save(a, request);
                            }
                            else {
                                return a;
                            }
                        }), map(a => {
                            return this.save(a, request);
                        }));
                    }
                    else {
                        return of(new HttpResponse({ body: data }));
                    }
                case false:
                    return next.handle(request).pipe(map(a => {
                        if (a instanceof HttpErrorResponse) {
                            return this.save(a, request);
                        }
                        else {
                            return a;
                        }
                    }), map(a => {
                        return this.save(a, request);
                    }));
            }
        }), catchError(response => {
            if (this.requestIsAthleteNewsTeaser(request) || this.requestIsAthletePhotos(request)) {
                return of(new HttpResponse({ body: { pager: { pages: 0, current_page: 0, next_page: 0, count: 0, }, results: [], error: true } }));
            }
            if (this.requestIsNews(request)) {
                return of(new HttpResponse({ body: [] }));
            }
            if (this.requestIsFromAPI(request)) {
                return of(new HttpResponse({
                    body: {
                        error: true,
                        items: [], "pageInfo": {
                            "totalResults": 0,
                            "resultsPerPage": 0,
                            "page": 0
                        },
                    }
                }));
            }
            return response;
        }));
    }
    /**
     * Method for check if the request is from WA API.
     *
     * @protected
     * @param {HttpRequest<any>} request The request sent.
     * @returns
     * @memberof CacheInterceptorService
     */
    requestIsFromAPI(request) {
        return request.params.has('content') && request.params.get('content') != '';
    }
    /**
     * Method for check if the request is from athlete photos Drupal API.
     *
     * @protected
     * @param {HttpRequest<any>} request The request sent.
     * @returns
     * @memberof CacheInterceptorService
     */
    requestIsAthletePhotos(request) {
        return request.url.includes(environment.photosAPI) || request.url.includes(environment.competitions_gallery);
    }
    /**
     * Method for check if the request is from news teaser Drupal API.
     *
     * @protected
     * @param {HttpRequest<any>} request The request sent.
     * @returns
     * @memberof CacheInterceptorService
     */
    requestIsAthleteNewsTeaser(request) {
        return request.url.includes(environment.news_api.athlete_news_teaser);
    }
    /**
     * Method for check if the request are from news in Drupal API.
     *
     * @protected
     * @param {HttpRequest<any>} request The request sent.
     * @returns
     * @memberof CacheInterceptorService
     */
    requestIsNews(request) {
        return request.url.includes(environment.news_api.athlete_news)
            || request.url.includes(environment.news_api.continental_association)
            || request.url.includes(environment.news_api.continental_association_teaser)
            || request.url.includes(environment.news_api.member_association_teaser)
            || request.url.includes(environment.news_api.member)
            || request.url.includes(environment.news_api.members)
            || request.url.includes(environment.news_api.athletes_spotligth)
            || request.url.includes(environment.news_api.athlete_news)
            || request.url.includes(environment.news_api.indoor);
    }
}
CacheInterceptorService.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function CacheInterceptorService_Factory() { return new CacheInterceptorService(i0.ɵɵinject(APP_MAX_EXPIRATION_TIME), i0.ɵɵinject(CacheStorageLayer), i0.ɵɵinject(IsOnlineService)); }, token: CacheInterceptorService, providedIn: "root" });
